import { Box, Stack, SxProps, Theme, Typography, TypographyProps } from '@mui/material';
import { ReactNode } from 'react';

export interface MediaListItemProps {
  leading?: ReactNode;
  media?: ReactNode;
  pretitle?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  size?: 'small' | 'medium' | 'large';
  onClick?(): void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const sizes = {
  small: {
    gap: 1.5,
    px: 1,
    py: 0.5,
    pretitleVariant: 'caption' as TypographyProps['variant'],
    titleVariant: 'body2' as TypographyProps['variant'],
    subtitleVariant: 'caption' as TypographyProps['variant'],
  },
  medium: {
    gap: 1.5,
    px: 1,
    py: 1,
    pretitleVariant: 'caption' as TypographyProps['variant'],
    titleVariant: 'body1' as TypographyProps['variant'],
    subtitleVariant: 'caption' as TypographyProps['variant'],
  },
  large: {
    gap: 2,
    px: 1,
    py: 1,
    pretitleVariant: 'caption' as TypographyProps['variant'],
    titleVariant: 'h6' as TypographyProps['variant'],
    subtitleVariant: 'body2' as TypographyProps['variant'],
  },
} as const;

export function MediaListItem({
  leading,
  media,
  pretitle,
  title,
  subtitle,
  trailing,
  size = 'medium',
  onClick,
  disabled,
  sx,
}: MediaListItemProps) {
  const s = sizes[size];

  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        px: s.px,
        py: s.py,
        borderRadius: 1,
        transition: 'background-color .15s',
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }),
        ...(disabled && {
          opacity: 0.5,
          pointerEvents: 'none',
        }),
        ...sx,
      }}
    >
      <Stack gap={s.gap} direction="row">
        {leading && (
          <Box
            onClick={(ev) => ev.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {leading}
          </Box>
        )}

        {media && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            {media}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {pretitle && (
            <Typography
              variant={s.pretitleVariant}
              color="text.secondary"
              noWrap
              sx={{ lineHeight: 1.25, letterSpacing: 0.5 }}
            >
              {pretitle}
            </Typography>
          )}

          <Typography variant={s.titleVariant} fontWeight={600} noWrap sx={{ lineHeight: 1.25 }}>
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant={s.subtitleVariant}
              color="text.secondary"
              noWrap
              sx={{ lineHeight: 1.25 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {trailing && (
          <Box
            onClick={(ev) => ev.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0,
              ml: 'auto',
            }}
          >
            {trailing}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
